import {addMinutes, differenceInMinutes, isAfter, isBefore} from 'date-fns'
import {Grid} from '@mui/material'
import {EditorDatePicker} from '../inputs/DatePicker'
import {FdsSelect} from '../common/FdsSelect'

/** Durations a manager actually schedules. */
const DURATIONS = [15, 30, 45, 60]

/** Never leave less than this much work on either side of a break. */
const MIN_WORK_MINUTES = 15

const DEFAULT_DURATION = 30

export const breakMinutes = (start: Date | null, end: Date | null): number =>
  start && end ? Math.max(0, differenceInMinutes(end, start)) : 0

/**
 * The latest a break of `duration` can start inside [shiftStart, shiftEnd].
 * Negative span means the shift is too short to hold it at all.
 */
const latestStart = (shiftStart: Date, shiftEnd: Date, duration: number): Date =>
  addMinutes(shiftEnd, -(duration + MIN_WORK_MINUTES))

const earliestStart = (shiftStart: Date): Date => addMinutes(shiftStart, MIN_WORK_MINUTES)

export const fits = (shiftStart: Date, shiftEnd: Date, duration: number): boolean =>
  differenceInMinutes(shiftEnd, shiftStart) >= duration + 2 * MIN_WORK_MINUTES

/**
 * Pull a break back inside its shift.
 *
 * Called whenever the shift's own times move: the API rejects a break that
 * pokes outside the shift, and silently dropping the manager's break would be
 * worse than sliding it. Returns nulls when the shift can no longer hold it.
 */
export const clampBreak = (
  shiftStart: Date,
  shiftEnd: Date,
  breakStart: Date | null,
  breakEnd: Date | null
): {break_start: Date | null; break_end: Date | null} => {
  const duration = breakMinutes(breakStart, breakEnd)
  if (!duration || !breakStart) {
    return {break_start: null, break_end: null}
  }
  if (!fits(shiftStart, shiftEnd, duration)) {
    return {break_start: null, break_end: null}
  }
  let start = breakStart
  const min = earliestStart(shiftStart)
  const max = latestStart(shiftStart, shiftEnd, duration)
  if (isBefore(start, min)) start = min
  if (isAfter(start, max)) start = max
  return {break_start: start, break_end: addMinutes(start, duration)}
}

/** A sensible first break: mid-shift, clamped to fit. */
export const defaultBreak = (shiftStart: Date, shiftEnd: Date) => {
  const duration = DEFAULT_DURATION
  const midpoint = addMinutes(shiftStart, differenceInMinutes(shiftEnd, shiftStart) / 2)
  return clampBreak(
    shiftStart,
    shiftEnd,
    addMinutes(midpoint, -duration / 2),
    addMinutes(midpoint, duration / 2)
  )
}

interface BreakFieldProps {
  shiftStart: Date
  shiftEnd: Date
  breakStart: Date | null
  breakEnd: Date | null
  onChange(breakStart: Date | null, breakEnd: Date | null): void
}

/**
 * The unpaid break on a shift.
 *
 * Start time plus a DURATION rather than two absolute pickers: a duration is
 * what managers think in ("a half-hour break"), and it turns "keep the break
 * inside the shift" into a clamp instead of an error the user has to fix.
 * The wire format is still two datetimes.
 */
const BreakField = ({shiftStart, shiftEnd, breakStart, breakEnd, onChange}: BreakFieldProps) => {
  const duration = breakMinutes(breakStart, breakEnd)
  const enabled = duration > 0
  const tooShort = !fits(shiftStart, shiftEnd, DEFAULT_DURATION)

  const toggle = (on: boolean) => {
    if (!on) {
      return onChange(null, null)
    }
    const next = defaultBreak(shiftStart, shiftEnd)
    onChange(next.break_start, next.break_end)
  }

  const setStart = (_name: string, date: Date) => {
    const next = clampBreak(shiftStart, shiftEnd, date, addMinutes(date, duration))
    onChange(next.break_start, next.break_end)
  }

  const setDuration = (minutes: number) => {
    const next = clampBreak(shiftStart, shiftEnd, breakStart, addMinutes(breakStart!, minutes))
    onChange(next.break_start, next.break_end)
  }

  return (
    <>
      <Grid item xs={12}>
        <div className='form-check form-switch'>
          <input
            className='form-check-input'
            type='checkbox'
            role='switch'
            id='shift-break-toggle'
            checked={enabled}
            disabled={tooShort && !enabled}
            onChange={(e) => toggle(e.target.checked)}
          />
          <label className='form-check-label fs-8' htmlFor='shift-break-toggle'>
            Unpaid break
            {tooShort && !enabled && (
              <span className='text-muted'> — shift is too short for one</span>
            )}
          </label>
        </div>
      </Grid>
      {enabled && (
        <>
          <Grid item sm={6} xs={12}>
            <EditorDatePicker
              label='Break start'
              name='break_start'
              value={breakStart!}
              onChange={setStart}
              minTime={earliestStart(shiftStart)}
              maxTime={latestStart(shiftStart, shiftEnd, duration)}
              minDate={shiftStart}
              maxDate={shiftEnd}
            />
          </Grid>
          <Grid item sm={6} xs={12}>
            {/* Medium size with a floating label, so it lines up with the
                break-start field beside it. */}
            <FdsSelect<number>
              id='shift-break-length'
              size='medium'
              width='100%'
              label='Break length'
              value={duration}
              onChange={setDuration}
              options={DURATIONS.filter((d) => fits(shiftStart, shiftEnd, d) || d === duration).map(
                (d) => ({value: d, label: `${d} minutes`})
              )}
            />
          </Grid>
        </>
      )}
    </>
  )
}

export default BreakField
